import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Pencil, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import MainLayout from "../layouts/MainLayout";
import AddEditBackupOutletMaster from "./AddEditBackupOutletMaster";

const BackupOutletMaster = () => {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    getOutletList();
  }, []);

  const filteredOutlets = outlets.filter((item) =>
    item.outletName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.outletKey?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.outletId?.toString().includes(searchTerm)
  );

  const getOutletList = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${API_URL}/BackupOutlet/GetAll`);

      setOutlets(response.data || []);
    } catch (error) {
      console.error(error);
      alert("Unable to load outlet data");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedOutlet(null);
    setShowForm(true);
  };

  const handleEdit = (row) => {
    setSelectedOutlet(row);
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setSelectedOutlet(null);

    // Refresh table after save/update
    getOutletList();
  };

  const exportToExcel = () => {
    const exportData = filteredOutlets.map((item) => ({
      OutletID: item.outletId,
      OutletName: item.outletName,
      OutletKey: item.outletKey,
      Status: item.status ? "Active" : "Inactive",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "BackupOutlet");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(file, "BackupOutletMaster.xlsx");
  };

  if (showForm) {
    return (
      <AddEditBackupOutletMaster data={selectedOutlet} onClose={handleClose} />
    );
  }

  return (
    <MainLayout>
      {/*
        Outer wrapper pinned to the viewport height below the app's own
        header/sidebar (MainLayout). h-full + flex-col + overflow-hidden
        means ONLY the table body scrolls — the topbar and footer stay fixed.
      */}
      <div className="flex flex-col h-full p-3 md:p-6">
        <div className="flex flex-col flex-1 min-h-0 bg-white rounded-xl shadow-sm border border-slate-200">
          {/*
            Topbar. On mobile the title stacks above the buttons (flex-col),
            which needs MORE height than a single row — so we use min-h here,
            never a fixed h-*, or the stacked content gets clipped/squeezed.
            The exact height is only locked once md:flex-row puts everything
            on one line, where the fixed height actually fits.
          */}
          <div className="flex-shrink-0 min-h-[88px] md:h-[76px] flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 px-4 md:px-6 py-3 md:py-0 border-b">
            <div className="leading-tight">
              <h2 className="text-lg md:text-xl font-bold text-slate-800">
                Backup Outlet Master
              </h2>
              <p className="text-xs md:text-sm text-slate-500">
                Manage Backup Outlet Configuration
              </p>
            </div>

            {/* Full-width 2-up grid on mobile for easier tapping; a normal
                inline row once there's room at md+ */}
            <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
              <input
                type="text"
                placeholder="Search Outlet ID, Name or Key..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-72 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <div className="grid grid-cols-2 gap-2 md:flex md:gap-2">
                <button
                  onClick={exportToExcel}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
                >
                  <FileSpreadsheet size={16} />
                  Excel
                </button>

                <button
                  onClick={handleAdd}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                >
                  <Plus size={16} />
                  Add Outlet
                </button>
              </div>
            </div>
          </div>

          {/*
            Body - the only scrollable region. flex-1 + min-h-0 lets it fill
            remaining space instead of pushing the footer off-screen.
            scrollbar-hide keeps scrolling but removes the visible bar.
          */}
          {/* Mobile-only hint — the table below stays wider than a phone
              screen on purpose (Excel-style dense columns), so it scrolls
              sideways rather than squeezing every column illegibly thin. */}
          <p className="flex-shrink-0 md:hidden px-4 py-1.5 text-[11px] text-slate-400 bg-slate-50 border-b">
            ← Swipe to see more columns
          </p>

          <div className="flex-1 min-h-0 overflow-auto scrollbar-hide">

            <table className="w-full min-w-[700px] border-collapse text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 border-b border-slate-300">
                  <th className="px-2 py-1.5 text-left font-semibold border border-slate-200">
                    Outlet ID
                  </th>
                  <th className="px-2 py-1.5 text-left font-semibold border border-slate-200">
                    Outlet Name
                  </th>
                  <th className="px-2 py-1.5 text-left font-semibold border border-slate-200">
                    Outlet Key
                  </th>
                  <th className="px-2 py-1.5 text-center font-semibold border border-slate-200">
                    Status
                  </th>
                  <th className="px-2 py-1.5 text-center font-semibold border border-slate-200">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center py-8 text-slate-500 border border-slate-200"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : filteredOutlets.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center py-8 text-slate-500 border border-slate-200"
                    >
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  filteredOutlets.map((item, idx) => (
                    <tr
                      key={item.id}
                      className={`border-b border-slate-200 hover:bg-slate-50 transition ${idx % 2 === 1 ? "bg-slate-50/50" : "bg-white"
                        }`}
                    >
                      <td className="px-2 py-1 border border-slate-200">
                        {item.outletId}
                      </td>

                      <td className="px-2 py-1 border border-slate-200">
                        {item.outletName}
                      </td>

                      <td className="px-2 py-1 border border-slate-200">
                        {item.outletKey}
                      </td>

                      <td className="px-2 py-1 border border-slate-200 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${item.status
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                            }`}
                        >
                          {item.status ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="px-2 py-1 border border-slate-200 text-center">
                        <button
                          onClick={() => handleEdit(item)}
                          className="inline-flex items-center justify-center p-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition"
                        >
                          <Pencil size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer - fixed height, never scrolls */}
          <div className="flex-shrink-0 px-4 md:px-6 py-2 border-t bg-slate-50 text-xs text-slate-600">
            Total Records : <strong>{filteredOutlets.length}</strong>
          </div>
        </div>
      </div>

      {/*
        Hides the scrollbar visually while keeping the element scrollable
        (Chrome/Safari/Edge via ::-webkit-scrollbar, Firefox via scrollbar-width).
        Add this once globally (e.g. in index.css) instead of per-component
        if you use scrollbar-hide elsewhere too.
      */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;     /* Firefox */
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;             /* Chrome, Safari, Opera */
        }
      `}</style>
    </MainLayout>
  );
};

export default BackupOutletMaster;